import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import PreviewBanner from "./PreviewBanner";

const LINES = [
  { p: "$ ", text: "phantom_os boot --tier=phantom", delay: 0.4 },
  { p: "> ", text: "verified_boot ................... OK", delay: 1.4 },
  { p: "> ", text: "google_play_services ............. REMOVED", delay: 1.9 },
  { p: "> ", text: "hardened_malloc .................. LOADED", delay: 2.4 },
  { p: "> ", text: "microphone_module ................ DISCONNECTED", delay: 2.9 },
  { p: "> ", text: "network_traffic .................. ROUTED", delay: 3.4 },
  { p: "> ", text: "vault status ..................... SEALED", delay: 3.9, red: true },
];

function TypedLine({ prefix, text, delay, red }) {
  const [shown, setShown] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      let i = 0;
      const iv = setInterval(() => {
        i++;
        setShown(text.slice(0, i));
        if (i >= text.length) clearInterval(iv);
      }, 22);
      return () => clearInterval(iv);
    }, delay * 1000);
    return () => clearTimeout(t);
  }, [text, delay]);
  return (
    <div className="font-mono text-xs md:text-sm text-[#EAEAEA] leading-loose">
      <span className="text-[#666]">{prefix}</span>
      <span className={red ? "text-[#7C3AED]" : ""}>{shown}</span>
      {shown.length < text.length && <span className="inline-block w-2 h-3 bg-[#EAEAEA] ml-1 align-middle animate-pulse" />}
    </div>
  );
}

export default function HeroTerminal() {
  return (
    <div
      className="min-h-[100svh] pt-24 relative overflow-hidden"
      data-testid="preview-hero-terminal"
      style={{
        backgroundColor: "#020202",
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
        backgroundSize: "26px 26px",
      }}
    >
      <PreviewBanner title="Cold Terminal" kind="Hero" />

      {/* scan overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 3px)",
        }}
      />

      <div className="px-6 md:px-10 grid grid-cols-12 gap-6 py-16">
        {/* Left: header */}
        <div className="col-span-12 md:col-span-5 z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="overline">
            /sys/manifest — phantom.node.au
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.15 }}
            className="font-display uppercase text-[13vw] md:text-[7.5vw] leading-[0.9] tracking-tight mt-8"
          >
            Cold.<br />
            Booted.<br />
            <span className="text-[#7C3AED]">Sealed.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 4.2 }}
            className="mt-10 max-w-md text-[#888] font-mono text-xs md:text-sm leading-relaxed"
          >
            Phantom Phone ships Google Pixel hardware with every attack surface Google
            baked in — removed, muted, or physically severed. No install wizard. No
            &quot;Sign in with your account&quot;. Just a locked device that boots to you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 4.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button className="btn-vault danger">Access catalogue</button>
            <button className="btn-ghost">$ read --manifesto</button>
          </motion.div>
        </div>

        {/* Right: terminal */}
        <div className="col-span-12 md:col-span-7 z-10">
          <div className="bg-[#050505] border border-[#1A1A1A] shadow-[0_0_60px_rgba(124,58,237,0.05)]">
            <div className="flex items-center justify-between border-b border-[#1A1A1A] px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 border border-[#333]" />
                <span className="w-2 h-2 border border-[#333]" />
                <span className="w-2 h-2 border border-[#7C3AED] bg-[#7C3AED]" />
              </div>
              <span className="overline text-[#444]">phantom-shell v14.2 — root@ghost</span>
              <span className="overline text-[#444]">SESSION 001</span>
            </div>
            <div className="p-6 md:p-10 min-h-[420px]">
              {LINES.map((l, i) => (
                <TypedLine key={i} prefix={l.p} text={l.text} delay={l.delay} red={l.red} />
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 4.6 }}
                className="mt-8 pt-4 border-t border-[#1A1A1A] font-mono text-xs text-[#666] flex justify-between"
              >
                <span>uptime: 00:00:04 · load: 0.02</span>
                <span className="text-[#7C3AED] blink">● connected</span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
